import React, { useState } from "react";
import { Download as DownloadIcon, Smartphone, CheckCircle2, Share, Chrome, ArrowRight, Info, QrCode, Check } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../../components/ui/button";
import { LogoMark } from "../../components/Logo";
import { useInstallPrompt } from "../../lib/pwa";
import { APP_STORES } from "../../lib/constants";

const DOWNLOAD_URL = typeof window !== "undefined" ? `${window.location.origin}/download` : "https://kohimataxi.com/download";
const SHARE_TEXT = "Get the KohimaTaxi & Delivery app — book rides and order food across Kohima.";

function ShareApp() {
  const [copied, setCopied] = useState(false);
  const share = async () => {
    const data = { title: "KohimaTaxi & Delivery", text: SHARE_TEXT, url: DOWNLOAD_URL };
    if (navigator.share) {
      try { await navigator.share(data); return; } catch { /* cancelled or unsupported — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(`${SHARE_TEXT} ${DOWNLOAD_URL}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { /* clipboard blocked */ }
  };
  return (
    <div className="mt-6 text-center">
      <Button onClick={share} variant="outline" className="rounded-full border-brand-200 px-5 text-brand hover:bg-brand-50" data-testid="share-app-button">
        {copied ? <><Check className="mr-2 h-4 w-4" /> Link copied</> : <><Share className="mr-2 h-4 w-4" /> Share app with friends</>}
      </Button>
      <p className="mt-2 text-xs text-ink-muted">Text the link or QR to anyone who wants the app.</p>
    </div>
  );
}

function ScanToOpen() {
  return (
    <div className="kt-card hidden w-full max-w-xs flex-col items-center p-6 text-center sm:flex" data-testid="qr-card">
      <p className="inline-flex items-center gap-2 text-sm font-semibold text-ink"><QrCode className="h-4 w-4 text-brand" /> Scan to open on your phone</p>
      <div className="mt-4 rounded-2xl bg-white p-3 ring-1 ring-surface-300">
        <QRCodeSVG value={DOWNLOAD_URL} size={168} level="M" fgColor="#111827" bgColor="#FFFFFF" data-testid="download-qr" />
      </div>
      <p className="mt-4 text-xs leading-relaxed text-ink-soft">Point your phone camera here to open KohimaTaxi and install it on your device.</p>
    </div>
  );
}

function InstallHero() {
  const { canInstall, install, installed, ios, standalone } = useInstallPrompt();
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const go = async () => {
    setBusy(true);
    const ok = await install();
    setBusy(false);
    if (ok) setDone(true);
  };

  const isInstalled = installed || standalone || done;

  return (
    <div className="kt-card mx-auto w-full max-w-xl p-7 text-center" data-testid="install-hero-card">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand-50">
        <LogoMark className="h-11 w-11" />
      </div>
      <h2 className="mt-5 text-xl font-bold text-ink">Install on this device</h2>

      {isInstalled ? (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-brand-50 px-4 py-2 text-sm font-semibold text-brand" data-testid="install-already">
          <CheckCircle2 className="h-4 w-4" /> KohimaTaxi is installed on this device
        </p>
      ) : canInstall ? (
        <>
          <p className="mt-2 text-sm text-ink-soft">One tap installs KohimaTaxi &amp; Delivery like a native app — its own window, a home-screen icon, and automatic updates.</p>
          <Button onClick={go} disabled={busy} className="mt-5 h-12 w-full rounded-full bg-brand text-base font-semibold text-white hover:bg-brand-600" data-testid="install-now-button">
            <DownloadIcon className="mr-2 h-5 w-5" /> {busy ? "Installing…" : "Install KohimaTaxi & Delivery"}
          </Button>
          <p className="mt-3 text-xs text-ink-muted">Works on Windows, macOS, ChromeOS and Android in Chrome or Edge.</p>
        </>
      ) : ios ? (
        <>
          <p className="mt-2 text-sm text-ink-soft">Add KohimaTaxi to your iPhone or iPad home screen from Safari.</p>
          <div className="mt-4 rounded-xl bg-surface-100 px-4 py-3 text-left text-sm text-ink-soft" data-testid="ios-steps">
            <p className="flex items-center gap-2"><Share className="h-4 w-4 text-brand" /> 1. Tap the <strong>Share</strong> button in Safari.</p>
            <p className="mt-2 flex items-center gap-2"><Smartphone className="h-4 w-4 text-brand" /> 2. Choose <strong>Add to Home Screen</strong>.</p>
          </div>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm text-ink-soft">Install KohimaTaxi &amp; Delivery straight from your browser — no app store needed.</p>
          <div className="mt-4 rounded-xl bg-surface-100 px-4 py-3 text-left text-sm text-ink-soft" data-testid="manual-install-steps">
            <p className="flex items-center gap-2"><Chrome className="h-4 w-4 text-brand" /> 1. Open the browser menu <strong>⋮</strong> (top-right) in Chrome or Edge.</p>
            <p className="mt-2 flex items-center gap-2"><DownloadIcon className="h-4 w-4 text-brand" /> 2. Click <strong>Install KohimaTaxi &amp; Delivery</strong> (or <strong>Apps → Install this site as an app</strong>).</p>
            <p className="mt-2 flex items-center gap-2"><Info className="h-4 w-4 text-brand" /> Or look for the install icon in the address bar.</p>
          </div>
          <p className="mt-3 text-xs text-ink-muted">Tip: if you just opened this page, the one-click button appears after a moment once your browser is ready.</p>
        </>
      )}
    </div>
  );
}

function StoreCard({ icon: Icon, platform, label, href, note, testId }) {
  const comingSoon = !href;
  const inner = (
    <div className={`kt-card flex items-center gap-4 p-5 transition-colors ${comingSoon ? "opacity-80" : "hover:border-brand-200"}`} data-testid={testId}>
      <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-ink text-white">
        <Icon className="h-6 w-6" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-ink-muted">{platform}</p>
        <p className="text-base font-semibold text-ink">{label}</p>
        <p className="text-xs text-ink-soft">{comingSoon ? "Coming soon" : note}</p>
      </div>
      {!comingSoon && <ArrowRight className="h-5 w-5 shrink-0 text-ink-muted" />}
    </div>
  );
  if (comingSoon) return inner;
  return <a href={href} target="_blank" rel="noopener noreferrer">{inner}</a>;
}

export default function Download() {
  return (
    <section className="kt-container py-16 lg:py-20">
      <div className="mx-auto max-w-3xl text-center">
        <p className="kt-eyebrow">Download</p>
        <h1 className="kt-h2 mt-3" data-testid="download-title">Get the KohimaTaxi &amp; Delivery app</h1>
        <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-ink-soft">
          Book rides, order food and track everything live. Install it on your phone, tablet or computer in seconds.
        </p>
      </div>

      <div className="mt-10 flex flex-col items-center justify-center gap-6 lg:flex-row lg:items-stretch">
        <InstallHero />
        <ScanToOpen />
      </div>

      <ShareApp />

      <div className="mx-auto mt-12 max-w-3xl">
        <h3 className="text-center text-sm font-semibold uppercase tracking-wider text-ink-muted">More ways to get the app</h3>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <StoreCard icon={Smartphone} platform="Android" label="Google Play" href={APP_STORES.android} note="Download for Android" testId="store-android" />
          <StoreCard icon={Smartphone} platform="iPhone &amp; iPad" label="App Store" href={APP_STORES.ios} note="Download for iOS" testId="store-ios" />
        </div>
        <p className="mt-6 text-center text-xs text-ink-muted">
          The web app (PWA) gives you the full experience on any device with a modern browser — no store account required.
        </p>
      </div>
    </section>
  );
}
